import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, MessageCircle, ShieldCheck, Sparkles } from 'lucide-react';
import { WHATSAPP_URL } from '../config/brand';

/**
 * Premium pricing + dual-CTA card for the hero right column.
 * Displays prominently on white card with theme accent.
 */
const THEME = {
  orange:  { bar: 'from-orange-500 to-amber-500',     pill: 'bg-orange-100 text-orange-700',   chip: 'bg-orange-50 text-orange-700 border-orange-200',   btn: 'bg-orange-600 hover:bg-orange-700',   ring: 'shadow-orange-500/30' },
  emerald: { bar: 'from-emerald-500 to-cyan-500',     pill: 'bg-emerald-100 text-emerald-700', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', btn: 'bg-emerald-600 hover:bg-emerald-700', ring: 'shadow-emerald-500/30' },
  amber:   { bar: 'from-amber-500 to-yellow-500',     pill: 'bg-amber-100 text-amber-700',     chip: 'bg-amber-50 text-amber-700 border-amber-200',     btn: 'bg-amber-600 hover:bg-amber-700',     ring: 'shadow-amber-500/30' },
  yellow:  { bar: 'from-amber-500 to-yellow-500',     pill: 'bg-amber-100 text-amber-700',     chip: 'bg-amber-50 text-amber-700 border-amber-200',     btn: 'bg-amber-600 hover:bg-amber-700',     ring: 'shadow-amber-500/30' },
  violet:  { bar: 'from-violet-500 to-purple-500',    pill: 'bg-violet-100 text-violet-700',   chip: 'bg-violet-50 text-violet-700 border-violet-200',   btn: 'bg-violet-600 hover:bg-violet-700',   ring: 'shadow-violet-500/30' },
  blue:    { bar: 'from-blue-500 to-cyan-500',        pill: 'bg-blue-100 text-blue-700',       chip: 'bg-blue-50 text-blue-700 border-blue-200',         btn: 'bg-blue-600 hover:bg-blue-700',       ring: 'shadow-blue-500/30' }
};

const HeroPricingCard = ({
  courseName = 'Course',
  oldPrice,
  newPrice,
  discountLabel,        // e.g. "63% OFF"
  badge = '',           // e.g. "100% JOB GUARANTEE" or "BESTSELLER"
  batchDate = 'New Batches Every Month',
  theme = 'orange',
  whatsappMessage = ''
}) => {
  const t = THEME[theme] || THEME.orange;
  const saving = (oldPrice && newPrice) ? (Number(oldPrice) - Number(newPrice)).toLocaleString('en-IN') : null;
  const wa = WHATSAPP_URL(whatsappMessage || `Hi, I want to know more about the ${courseName}`);

  return (
    <div
      data-testid="hero-pricing-card"
      className={`relative bg-white rounded-3xl shadow-2xl ${t.ring} overflow-hidden border border-white/40`}
    >
      {/* Top accent bar */}
      <div className={`h-1.5 w-full bg-gradient-to-r ${t.bar}`}></div>

      <div className="p-6 md:p-7">
        {badge && (
          <div className={`inline-flex items-center gap-1.5 ${t.pill} rounded-full px-3 py-1 mb-4`}>
            <Sparkles className="w-3.5 h-3.5" />
            <span className="text-[11px] font-black uppercase tracking-widest">{badge}</span>
          </div>
        )}

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Limited-Time Pricing</p>
        <h3 className="text-lg font-bold text-slate-900 mb-4 leading-snug">{courseName}</h3>

        {/* Price block */}
        {newPrice ? (
          <div className="mb-5">
            <div className="flex items-baseline gap-3 flex-wrap">
              {oldPrice && (
                <span className="text-xl text-slate-400 line-through font-semibold">₹{Number(oldPrice).toLocaleString('en-IN')}</span>
              )}
              <span className="text-4xl md:text-5xl font-black text-slate-900 leading-none">
                ₹{Number(newPrice).toLocaleString('en-IN')}
              </span>
            </div>
            {(saving || discountLabel) && (
              <div className="mt-2 inline-flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-full px-3 py-1">
                <span className="text-xs font-black tracking-wide">
                  {saving && `You Save ₹${saving}`}{saving && discountLabel ? ' · ' : ''}{discountLabel}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="mb-5">
            <p className="text-3xl md:text-4xl font-black text-slate-900 leading-tight">{discountLabel || 'Talk to Counselor'}</p>
            <p className="text-xs text-slate-500 mt-1">Speak with our team for fee &amp; EMI details</p>
          </div>
        )}

        {/* Batch date */}
        <div className={`flex items-center gap-2 text-sm border rounded-xl px-3 py-2 mb-5 ${t.chip}`}>
          <Calendar className="w-4 h-4 flex-shrink-0" />
          <span><strong>Status:</strong> {batchDate} · <strong>Limited seats</strong></span>
        </div>

        {/* Primary CTA — Book Free Demo */}
        <Link
          to="/book-demo"
          data-testid="hero-book-demo-btn"
          className={`w-full inline-flex items-center justify-center gap-2 ${t.btn} text-white font-black py-3.5 rounded-xl text-base shadow-lg hover:shadow-xl transition-all mb-3`}
        >
          Book a Free Demo <ArrowRight className="w-5 h-5" />
        </Link>

        {/* WhatsApp CTA */}
        <a
          href={wa}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hero-whatsapp-btn"
          className="w-full inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-xl text-sm shadow-md transition-all"
        >
          <MessageCircle className="w-4 h-4" /> WhatsApp Now
        </a>

        <p className="text-[11px] text-slate-400 text-center mt-4 inline-flex items-center justify-center gap-1 w-full">
          <ShieldCheck className="w-3 h-3" /> 100% Free demo · No payment · No obligation
        </p>
      </div>
    </div>
  );
};

export default HeroPricingCard;
